"use client";
import React from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";
import { motion, useScroll, useTransform } from "framer-motion";

// Interfaces
interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}

// Main Component
export default function Content_Home_Layout2({ data }: ContentProps) {
    const pathname = usePathname();
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);
    const bgColor = data.bgColor || "#1A202C"; // A dark background for a bold feel

    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, 200]); // A subtle parallax effect

    // if (!data.isLive) return null;

    return (
        <section className="relative w-full overflow-hidden text-white bg-[#1A202C] min-h-[700px] flex items-center py-20 lg:py-0">
            <div className="absolute inset-0 bg-[url('/assets/diagonal-stripes.svg')] bg-cover opacity-10"></div>

            <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                {/* Text Content */}
                <div className={`flex flex-col justify-center ${data.isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="max-w-xl">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <p className="text-sm uppercase tracking-[0.2em] font-light text-gray-400 mb-4">{data.subtitle || "The New Standard"}</p>
                            {data.useH1Tag ? (
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                    {data.title}
                                </h1>
                            ) : (
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                    {data.title}
                                </h2>
                            )}
                        </motion.div>

                        {data.contentTexts?.map((item, index) => (
                            <motion.div
                                key={index}
                                className="mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 * (index + 1) }}
                                viewport={{ once: true }}
                            >
                                <h3 className="text-xl font-semibold text-gray-200 mb-2">{item.title}</h3>
                                <ReactMarkdown className="prose prose-sm text-gray-400">
                                    {item.text}
                                </ReactMarkdown>
                            </motion.div>
                        ))}

                        {data.button && data.button.length > 0 && (
                            <motion.div
                                className="mt-8"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <Link
                                    href={data.button[0].url}
                                    target={data.button[0].newTab ? '_blank' : '_self'}
                                    className="inline-block px-8 py-3 text-lg font-bold text-[#1A202C] bg-white rounded-full hover:bg-gray-200 transition-colors duration-300 transform hover:scale-105"
                                >
                                    {data.button[0].text}
                                </Link>
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Image Content */}
                <div className={`relative w-full h-[500px] lg:h-full flex items-center justify-center ${data.isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="relative w-full h-full lg:w-4/5 lg:h-4/5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="w-full h-full"
                            style={{ y }}
                        >
                            <Image
                                src={imgUrl || "/placeholder.jpg"}
                                alt={data.picture?.data?.attributes?.alternativeText || "Modern product showcase"}
                                fill
                                className="object-cover rounded-xl shadow-2xl"
                                sizes="(max-width: 768px) 100vw, 50vw"
                                priority
                            />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}